import type { MapDefJson } from '../board/mapSchema';

// GameDef — the modularity seam's content contract (plan §5.1, brief §6.2). Declaration
// ORDER is normative: resource order drives draw flattening (plan §9.3), shelf pedestals
// (plan §16.3) and discard tie-breaks (plan §13.4); module order drives hook order (plan
// §5.2). Composition/validation machinery lands in engine/registry.ts (H1-S2).

export type ResourceId = string;
export type TileId = string;
export type PieceId = string;
export type CardId = string;
export type ModuleId = string;

/** Sparse non-negative-integer bag; keys present only with value ≥ 1 (plan §3.1). */
export type ResourceBag = Record<ResourceId, number>;

export interface ResourceDef {
  id: ResourceId;
  display: string;
  /** Tile/pedestal color (plan §17.7 palette). */
  color: string;
  /** Shelf token recipe key (plan §16.3). */
  tokenKey: string;
}

export interface TileDef {
  id: TileId;
  produces: ResourceId | null;
  color: string;
  /** Module-interpreted behavior key (plan §6.7 extension surface); null for plain tiles. */
  special: string | null;
  /**
   * H20-S1: holds a number disc although it produces no FIXED resource (a gold field pays a
   * resource of the owner's choice, through its module). Optional and absent on every classic
   * tile, so the classic def JSON does not change.
   */
  numbered?: true;
}

/** Does this tile take a number disc? (producing tiles, and the ones that declare `numbered`) */
export function carriesDisc(tile: Pick<TileDef, 'produces' | 'numbered'>): boolean {
  return tile.produces !== null || tile.numbered === true;
}

export interface PieceDef {
  id: PieceId;
  domain: 'vertex' | 'edge' | 'hex';
  /** Per-seat pool size (hard limit, outline §2/§7). */
  pool: number;
  vp: number;
}

export interface DeckCardDef {
  card: CardId;
  count: number;
  /** Effect key resolved by a module (plan §5.1). */
  effect: string;
}

export interface DeckDef {
  id: string;
  cards: DeckCardDef[];
}

export type TurnMode = 'classic' | 'paired' | 'sbp';

export type MapSpec =
  | { kind: 'fixed'; mapId: string }
  | { kind: 'generated'; seats: number }
  | { kind: 'custom'; json: MapDefJson };

export interface GameDef {
  resources: ResourceDef[];
  tiles: TileDef[];
  pieces: PieceDef[];
  /** pieceId or 'advance-card' → cost bag (plan §6.2). */
  costs: Record<string, ResourceBag>;
  decks: DeckDef[];
  map: MapSpec;
  victory: { target: number; sources: string[] };
  turnMode: TurnMode;
  /** The exact ordered module list — omission = removal (plan §5.2). */
  modules: ModuleId[];
}

// ── ResourceBag helpers (pure; canonical-form preserving) ────────────────────

/** Total card count in a bag. */
export function bagTotal(bag: ResourceBag): number {
  let n = 0;
  for (const k of Object.keys(bag)) n += bag[k] ?? 0;
  return n;
}

/** a + b as a fresh canonical bag. */
export function bagAdd(a: ResourceBag, b: ResourceBag): ResourceBag {
  const out: ResourceBag = {};
  for (const src of [a, b]) {
    for (const k of Object.keys(src)) {
      const v = (out[k] ?? 0) + (src[k] ?? 0);
      if (v > 0) out[k] = v;
    }
  }
  return out;
}

/** a − b as a fresh canonical bag. Throws never — caller must have checked bagContains. */
export function bagSubtract(a: ResourceBag, b: ResourceBag): ResourceBag {
  const out: ResourceBag = {};
  for (const k of Object.keys(a)) {
    const v = (a[k] ?? 0) - (b[k] ?? 0);
    if (v > 0) out[k] = v;
  }
  return out;
}

/** Does a cover b (a ⊇ b, per key)? */
export function bagContains(a: ResourceBag, b: ResourceBag): boolean {
  for (const k of Object.keys(b)) if ((a[k] ?? 0) < (b[k] ?? 0)) return false;
  return true;
}

/** Keys shared by both bags (the like-for-like ban reads this, plan §8.2). */
export function bagOverlap(a: ResourceBag, b: ResourceBag): boolean {
  for (const k of Object.keys(b)) if ((b[k] ?? 0) > 0 && (a[k] ?? 0) > 0) return true;
  return false;
}

/** Canonical-form check: integer values ≥ 1 only (plan §3.1; fuzz asserts it everywhere). */
export function bagIsCanonical(bag: ResourceBag): boolean {
  for (const k of Object.keys(bag)) {
    const v = bag[k];
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 1) return false;
  }
  return true;
}
